import { useEffect } from 'react';
import type { AccessExtended } from '@youscience/bpa-common';
import useAuthStore from '@store/AuthStore';
import useCommonAppStore from '@store/CommonAppStore';
import { shallow } from 'zustand/shallow';
import { UserRoles } from '@interfaces/user';

interface UseAnyCommonAppEnabledReturn {
  currentHasCommonApp: boolean;
  anyTenantHasCommonApp: boolean;
  tenantsWithCommonApp: AccessExtended[];
  hasMultipleAccesses: boolean;
  isLearner: boolean;
}

let isInitialFetchStarted = false;

export const useAnyCommonAppEnabled = (): UseAnyCommonAppEnabledReturn => {
  const { currentAccess, allAccesses, isLearner } = useAuthStore(
    (state) => ({
      currentAccess: state.authSession.currentAccess,
      allAccesses: state.authSession.userData?.access || [],
      isLearner: state.authSession.currentAccess?.role === UserRoles.Learner,
    }),
    shallow,
  );

  const { getApplicant, applicant } = useCommonAppStore((state) => ({
    getApplicant: state.getApplicant,
    applicant: state.applicant,
  }));

  const currentHasCommonApp = currentAccess?.commonAppEnabled || false;

  const tenantsWithCommonApp = allAccesses.filter((access: AccessExtended) => {
    return access.tenant?.productAvailability?.commonApp === true;
  });

  const anyTenantHasCommonApp = isLearner ? currentHasCommonApp : tenantsWithCommonApp.length > 0;

  useEffect(() => {
    if (anyTenantHasCommonApp && isLearner && !applicant && !isInitialFetchStarted) {
      isInitialFetchStarted = true;
      getApplicant();
    }
  }, [anyTenantHasCommonApp, isLearner, getApplicant, applicant]);

  return {
    currentHasCommonApp,
    anyTenantHasCommonApp,
    tenantsWithCommonApp,
    hasMultipleAccesses: allAccesses.length > 1,
    isLearner,
  };
};
